/**
 * AccessKeyGate — passcode prompt for the public-access build
 * (docs/SPEC-public-access.md).
 *
 * The Pi rejects unauthenticated API calls with 401; `agentFetch`
 * broadcasts those, and this overlay appears asking for the passcode.
 * Saving stores it in localStorage (`reefapp.accessKey`) and reloads
 * the page so every data hook refetches with the key attached. One-time
 * per device.
 */
import { useEffect, useState } from 'react';

import { onUnauthorized, readAccessKey, writeAccessKey } from '../lib/agent-fetch';
import { Button } from './Button';
import { Card, CardBody, CardHeader, CardTitle } from './Card';

export function AccessKeyGate() {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [hadKey, setHadKey] = useState(false);

  useEffect(() => {
    return onUnauthorized(() => {
      setHadKey(readAccessKey() !== null);
      setOpen(true);
    });
  }, []);

  if (!open) return null;

  function save() {
    if (value.trim().length === 0) return;
    writeAccessKey(value);
    window.location.reload();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
      data-testid="access-key-gate"
      role="dialog"
      aria-modal="true"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Enter your ReefApp passcode</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-reef-200 mb-3">
            {hadKey
              ? 'The saved passcode was not accepted — it may have changed. Enter the current passcode to reconnect.'
              : 'This device hasn’t been unlocked yet. Enter the passcode once and it will be remembered here.'}
          </p>
          <input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
            }}
            autoFocus
            className="w-full rounded-md border border-reef-700/60 bg-reef-900/60 px-2.5 py-1.5 text-sm text-reef-100"
            data-testid="access-key-input"
          />
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              data-testid="access-key-cancel"
            >
              Not now
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={value.trim().length === 0}
              onClick={save}
              data-testid="access-key-save"
            >
              Unlock
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
